import { QueryClient, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityLogDto,
  CreateProductAreaDto,
  FlattenedProductAreaItemDto,
  GetProductAreaArchiveCheckResponseDto,
  GetProductAreaSummaryResponseDto,
  LifecycleFilterType,
  ProductAreaDto,
  ProductAreaStatsDto,
  UpdateProductAreaDto,
} from '@tempo-io/fim3-dto';
import { type GetAllParamsDto, LifecycleFilter } from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useRef } from 'react';
import { getEnv } from '@/lib/utils/common/getEnv';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';

/** Shared stale time for /product-areas/:id/stats queries.
 * Both useGetProductAreaStats and useProductAreaStatsMultiple share the same
 * TanStack Query cache key, so they must agree on freshness.
 */
export const PRODUCT_AREA_STATS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

interface ApiUserResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  [key: string]: unknown; // For other properties from the spread operator
}

export const useProductAreasApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/product-areas`;
  const queryClientWithoutError = useRef(new QueryClient());
  const defaultLimit = 25;

  const createProductArea = async (
    productArea: CreateProductAreaDto,
  ): Promise<{ data: ProductAreaDto }> => {
    const { data } = await httpClient.post(baseUrl, productArea);
    return data;
  };

  const getProductAreas = async (
    params?: GetAllParamsDto,
  ): Promise<GetProductAreaSummaryResponseDto> => {
    const { data } = await httpClient.get(baseUrl, {
      params: { ...params },
    });
    return data;
  };

  const getProductAreasByIds = async (ids: number[]): Promise<{ data: ProductAreaDto[] }> => {
    const { data } = await httpClient.get(`${baseUrl}/getByIds`, { params: { ids } });
    return data;
  };

  const getProductArea = async (
    id: number,
    lifecycleFilter?: LifecycleFilterType,
  ): Promise<{ data: ProductAreaDto }> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}`, { params: { lifecycleFilter } });
    return data;
  };

  const getProductAreaChildren = async (
    params: GetAllParamsDto & {
      searchTerm?: string;
      parentProductAreaId: number;
    },
  ): Promise<GetProductAreaSummaryResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${params.parentProductAreaId}/children`, {
      params: { ...params },
    });
    return data;
  };

  const getProductAreaArchiveCheck = async (
    id?: number,
    includeDeleted?: boolean,
  ): Promise<GetProductAreaArchiveCheckResponseDto | undefined> => {
    if (id === undefined || id === -1) return undefined;

    const { data } = await httpClient.get(`${baseUrl}/${id}/archive-check`, {
      params: { includeDeleted },
    });
    return data;
  };

  const getUserPermissions = async (userId?: string) => {
    const { data } = await httpClient.get(`${baseUrl}/permissions/user`, {
      params: { userId },
    });
    return data;
  };

  const updateProductArea = async (
    updatedProductArea: UpdateProductAreaDto & { id: number },
  ): Promise<{ data: ProductAreaDto }> => {
    const { data } = await httpClient.put(`${baseUrl}/${updatedProductArea.id}`, {
      ...updatedProductArea,
      id: undefined,
    });
    return data;
  };

  const removeProductArea = async (id: number) => {
    const { data } = await httpClient.delete(`${baseUrl}/${id}`);
    return data;
  };

  const archiveProductArea = async (id: number) => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/archive`);
    return data;
  };

  const restoreProductArea = async (id: number) => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/restore`);
    return data;
  };

  const queryClient = useQueryClient();

  const getProductAreasQueryKey = 'product-areas';

  // biome-ignore lint/suspicious/noExplicitAny: Activity log structure is dynamic
  const extractPermissionUserIds = (logs: any[]): string[] => {
    const userIds: string[] = [];

    // biome-ignore lint/suspicious/noExplicitAny: Activity log structure is dynamic
    logs.forEach((log: any) => {
      // biome-ignore lint/suspicious/noExplicitAny: ChangeLog structure is dynamic
      log.changeLog?.forEach((change: any) => {
        if (change.entity?.startsWith('permission') && change.metadata?.userId) {
          userIds.push(change.metadata.userId);
        }
      });
    });

    return [...new Set(userIds)].filter(Boolean);
  };

  const formatPermissionChange = (
    metadata: {
      userId?: string;
      teamId?: string;
      role: string;
      oldRole?: string;
      isRemove?: boolean;
    },
    userMap: Map<string, string>,
  ): string => {
    const { userId, teamId } = metadata;
    const displayName = userId ? `user ${userMap.get(userId) || userId}` : `Team ${teamId}`;

    let text = `Assigned role '${metadata.role}' to ${displayName}`;
    if (metadata.isRemove) {
      text = `Removed role for ${displayName}`;
    }
    return text;
  };

  // biome-ignore lint/suspicious/noExplicitAny: Activity log structure is dynamic
  const enrichPermissionLogs = (result: any[], userMap: Map<string, string>): void => {
    // biome-ignore lint/suspicious/noExplicitAny: Activity log structure is dynamic
    result.forEach((log: any) => {
      if (!log.changeLog) return;

      // biome-ignore lint/suspicious/noExplicitAny: ChangeLog structure is dynamic
      log.changeLog.forEach((change: any) => {
        if (change.entity?.startsWith('permission') && change.metadata) {
          change.entity = 'permission';
          change.value = formatPermissionChange(change.metadata, userMap);
        }
      });
    });
  };

  const getProductActivityLog = async (id: number, logType = '') => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/activity-logs`, {
      params: { logType },
    });

    const actorUserIds = data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(
      httpClient,
      actorUserIds,
      data,
      // biome-ignore lint/suspicious/noExplicitAny: getUserDataForResults requires IData type but activity logs have userId field
      (log: any) => log.userId,
    );

    const permissionUserIds = extractPermissionUserIds(result);

    if (permissionUserIds.length > 0) {
      const { data: userData } = await httpClient.get(`${getEnv().apiBaseUrl}/api/users/v1`, {
        params: {
          limit: '50',
          filter: JSON.stringify({
            field: 'id',
            operator: 'in',
            value: permissionUserIds,
          }),
        },
      });

      const userMap = new Map<string, string>(
        // biome-ignore lint/suspicious/noExplicitAny: User API response structure is dynamic
        userData.items.map((u: any) => [u.id, u.name ?? u.email ?? 'Unknown'] as [string, string]),
      );

      enrichPermissionLogs(result, userMap);
    }

    return result;
  };

  const useGetProductActivityLog = (id: number, logType = '') =>
    useQuery({
      queryKey: [getProductAreasQueryKey, id, 'activity-logs', logType],
      queryFn: () => getProductActivityLog(id, logType),
    });

  const useGetAll = (params?: GetAllParamsDto) => {
    const limit = params?.limit ?? defaultLimit;
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: [baseUrl, 'GetAll', params?.lifecycleFilter, params],
      queryFn: ({ pageParam }) => {
        const newParams = { ...params, limit, offset: pageParam * limit };
        return getProductAreas(newParams);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined;
      },
    });
  };

  const useGetProductAreaChildren = (
    params: GetAllParamsDto & {
      searchTerm?: string;
      parentProductAreaId: number;
    },
  ) => {
    const limit = params?.limit ?? defaultLimit;
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: [getProductAreasQueryKey, 'children', 'GetAll', params?.lifecycleFilter, params],
      queryFn: ({ pageParam }) => {
        const newParams = { ...params, limit, offset: pageParam * limit };
        return getProductAreaChildren(newParams);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined;
      },
    });
  };

  const useGetOne = (
    id: number | undefined,
    suppressNotFoundError?: boolean,
    // lifecycleFilter?: LifecycleFilter,
  ) =>
    useQuery(
      {
        queryKey: [getProductAreasQueryKey, id],
        queryFn: () => (id ? getProductArea(id) : undefined),
        enabled: !!id,
        retry: !suppressNotFoundError,
      },
      suppressNotFoundError ? queryClientWithoutError.current : queryClient,
    );

  const useGetSome = (ids: number[]) =>
    useQuery({
      queryKey: [baseUrl, ids],
      queryFn: () => getProductAreasByIds(ids),
      enabled: ids && ids.length > 0,
    });

  const useGetArchiveCheck = (id?: number, includeDeleted?: boolean) =>
    useQuery({
      queryKey: [baseUrl, id, includeDeleted],
      queryFn: () => getProductAreaArchiveCheck(id),
    });

  const useGetUserPermissions = (userId?: string) =>
    useQuery({
      queryKey: [baseUrl, 'permissions', 'user', userId],
      queryFn: () => getUserPermissions(userId),
    });

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createProductArea,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [getProductAreasQueryKey] });
      },
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: updateProductArea,
      onSuccess: (data) =>
        queryClient.invalidateQueries({
          queryKey: [getProductAreasQueryKey, data.data.id],
        }),
    });

  const useRemove = () =>
    useErrorHandlingMutation({
      mutationFn: removeProductArea,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [getProductAreasQueryKey] }),
    });

  const useArchive = () =>
    useErrorHandlingMutation({
      mutationFn: archiveProductArea,
      onSuccess: (data) =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [getProductAreasQueryKey, data.data.id],
          }),
          queryClient.resetQueries({
            queryKey: [getProductAreasQueryKey, 'GetAll', LifecycleFilter.Active],
          }),
        ]),
    });

  const useRestore = () =>
    useErrorHandlingMutation({
      mutationFn: restoreProductArea,
      onSuccess: (data) =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [getProductAreasQueryKey, data.data.id],
          }),
          queryClient.resetQueries({
            queryKey: [getProductAreasQueryKey, 'GetAll', LifecycleFilter.Active],
          }),
        ]),
    });

  const getUsersBySearchTerm = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      return { items: [] };
    }

    const { data } = await httpClient.get(`${getEnv().apiBaseUrl}/api/users/v1`, {
      params: {
        filter: JSON.stringify({
          OR: [
            { field: 'name', operator: 'like', value: searchTerm },
            { field: 'email', operator: 'like', value: searchTerm },
          ],
        }),
        limit: 50,
      },
    });
    return data?.items?.map((element: ApiUserResponse) => {
      return {
        ...element,
        EmailAddress: element.email,
        FirstName: element.firstName,
        LastName: element.lastName,
        UserID: element.id,
      };
    });
  };

  const useGetUsersBySearchTerm = ({ searchTerm }: { searchTerm: string }) =>
    useQuery({
      queryKey: [baseUrl, 'getUsersBySearch', searchTerm],
      queryFn: () => getUsersBySearchTerm(searchTerm),
      enabled: !!searchTerm.trim(),
    });

  const getInitialUsers = async () => {
    const { data } = await httpClient.get(`${getEnv().apiBaseUrl}/api/users/v1`, {
      params: { limit: 25 },
    });
    return (data?.items ?? []).map((element: ApiUserResponse) => {
      return {
        ...element,
        EmailAddress: element.email,
        FirstName: element.firstName,
        LastName: element.lastName,
        UserID: element.id,
      };
    });
  };

  const useGetInitialUsers = () =>
    useQuery({
      queryKey: [baseUrl, 'getInitialUsers'],
      queryFn: getInitialUsers,
      staleTime: 5 * 60 * 1000,
    });

  const getAccountData = async (userIds: string[]) => {
    const { data } = await httpClient.get(`${getEnv().apiBaseUrl}/api/users/v1`, {
      params: {
        limit: 50,
        filter: JSON.stringify({
          field: 'id',
          operator: 'in',
          value: userIds,
        }),
      },
    });
    return data?.items?.map((element: ApiUserResponse) => {
      return {
        ...element,
        EmailAddress: element.email,
        FirstName: element.name,
        LastName: element.lastName,
        UserID: element.id,
      };
    });
  };

  const useGetAccountData = ({ userIds }: { userIds: string[] }) =>
    useQuery({
      queryKey: [baseUrl, 'getAccountUsers', userIds],
      queryFn: () => getAccountData(userIds),
      enabled: userIds && userIds.length > 0,
    });

  const getTeams = async (nameSearchValue?: string) => {
    if (!nameSearchValue?.trim()) {
      return { items: [] };
    }

    const { data } = await httpClient.get(`${getEnv().apiBaseUrl}/api/groups/v1`, {
      params: {
        filter: JSON.stringify({
          OR: [{ field: 'name', operator: 'like', value: nameSearchValue }],
        }),
        limit: 50,
      },
    });
    return data;
  };

  const useFindTeams = ({ nameSearchValue }: { nameSearchValue?: string }) =>
    useQuery({
      queryKey: [baseUrl, 'getTeams', nameSearchValue],
      queryFn: () => getTeams(nameSearchValue),
      enabled: !!nameSearchValue?.trim(),
    });

  const getTeamMembersList = async (teamId: string) => {
    if (!teamId?.trim()) {
      return { items: [] };
    }

    const { data } = await httpClient.get(`${getEnv().apiBaseUrl}/api/groups/v1/${teamId}/members`);
    return data;
  };

  const getFlattenedProductAreas = async (): Promise<{ data: FlattenedProductAreaItemDto[] }> => {
    const { data } = await httpClient.get(`${baseUrl}/flat`);
    return data;
  };

  const useGetFlattenedProductAreas = () =>
    useQuery({
      queryKey: [getProductAreasQueryKey, 'flat'],
      queryFn: getFlattenedProductAreas,
    });

  const getProductAreaStats = async (
    productAreaId: number,
  ): Promise<{ data: ProductAreaStatsDto }> => {
    const { data } = await httpClient.get(`${baseUrl}/${productAreaId}/stats`);
    return data;
  };

  const useGetProductAreaStats = (productAreaId: number, enabled = true) =>
    useQuery({
      queryKey: [getProductAreasQueryKey, 'stats', productAreaId],
      queryFn: () => getProductAreaStats(productAreaId),
      enabled: productAreaId > 0 && enabled,
      staleTime: PRODUCT_AREA_STATS_STALE_TIME,
    });

  const getAggregateProductAreaStats = async (): Promise<{ data: ProductAreaStatsDto }> => {
    const { data } = await httpClient.get(`${baseUrl}/stats`, { params: { all: true } });
    return data;
  };

  const useGetAggregateProductAreaStats = (enabled = true) =>
    useQuery({
      queryKey: [getProductAreasQueryKey, 'stats', 'all'],
      queryFn: getAggregateProductAreaStats,
      enabled,
    });

  const useGetTeamMembersList = ({ teamId }: { teamId: string }) =>
    useQuery({
      queryKey: [baseUrl, 'getTeamMembersList', teamId],
      queryFn: () => getTeamMembersList(teamId),
      enabled: Boolean(teamId),
    });

  return {
    getProductAreaChildren,
    useCreate,
    useUpdate,
    useGetOne,
    useGetSome,
    useGetAll,
    useGetFlattenedProductAreas,
    useGetProductAreaStats,
    useGetAggregateProductAreaStats,
    useGetProductAreaChildren,
    useRemove,
    useArchive,
    useRestore,
    useGetProductActivityLog,
    useGetArchiveCheck,
    useGetUserPermissions,
    useGetUsersBySearchTerm,
    useGetInitialUsers,
    useGetAccountData,
    useFindTeams,
    useGetTeamMembersList,
  };
};
